// features/findings/components-shared.js
// ════════════════════════════════════════════════════════════════════
// Shared finding-article components used across multiple findings.
// Form-specific components (CaseCard, SignalCheck, MechanismStep,
// WedgePanel, etc.) live in their respective form-component files.
// ════════════════════════════════════════════════════════════════════

import Link from 'next/link';

export function FindingBack({ href = '/findings', label = '← Findings index' }) {
  return (
    <Link href={href} className="finding-back">
      {label}
    </Link>
  );
}

export function FindingHeader({ eyebrow, title, sub, subItalic, meta, allVersions }) {
  return (
    <header className="finding-header">
      <div className="finding-eyebrow">{eyebrow}</div>
      <h1 className="finding-title">{title}</h1>
      {sub && <p className="finding-sub">{sub}</p>}
      {subItalic && <p className="finding-sub-italic">{subItalic}</p>}
      {meta && <VersionStatus meta={meta} allVersions={allVersions} />}
      {meta?.whatChanged && <WhatChangedBanner meta={meta} />}
    </header>
  );
}

export function ChartSlot({ kind, label, description }) {
  return (
    <div className={`finding-chart-slot finding-chart-${kind}`}>
      <div className="finding-chart-slot-eyebrow">Chart slot · {kind} · renders Phase F3</div>
      <div className="finding-chart-slot-label">{label}</div>
      <div className="finding-chart-slot-desc">{description}</div>
    </div>
  );
}

export function FindingPill({ tone = 'neutral', children }) {
  return <span className={`finding-pill finding-pill-${tone}`}>{children}</span>;
}

export function FindingPillRow({ children }) {
  return <div className="finding-pill-row">{children}</div>;
}

export function FindingSection({ eyebrow, title, children }) {
  return (
    <section className="finding-section">
      <div className="finding-section-eyebrow">{eyebrow}</div>
      <h2 className="finding-section-title">{title}</h2>
      {children}
    </section>
  );
}

export function FindingSubhead({ children }) {
  return <h3 className="finding-subhead">{children}</h3>;
}

/**
 * FindingCite — render a citation handle as a clickable link to its
 * canonical URL. Falls back to a plain <code> if the handle doesn't
 * parse as a valid lab:finding handle.
 *
 * Handle format: lab:finding/<domain>/<year>/<slug>/<version>
 * Versioned URL: /findings/<domain>/<year>/<slug>/<version>
 * Bare URL:      /findings/<domain>/<year>/<slug>  (resolves to latest)
 */
export function FindingCite({ handle }) {
  if (!handle || typeof handle !== 'string') return null;
  const match = handle.match(/^lab:finding\/([^/]+)\/(\d+)\/([^/]+?)(?:\/(v\d+))?$/);
  if (!match) {
    return <code className="finding-cite-handle">{handle}</code>;
  }
  const [, domain, year, slug, version] = match;
  const href = version
    ? `/findings/${domain}/${year}/${slug}/${version}`
    : `/findings/${domain}/${year}/${slug}`;
  return (
    <Link href={href} className="finding-cite">
      <code className="finding-cite-handle">{handle}</code>
    </Link>
  );
}

/**
 * Lifecycle status badge — surfaces the finding's version + lifecycle state
 * near the article header. Renders as a small pill with optional tone class.
 */
export function VersionStatus({ meta, allVersions = [] }) {
  if (!meta) return null;
  const { version, lifecycle, visibility, prevVersion, revisedBy, retractionNote } = meta;

  const lifecycleTone =
    lifecycle === 'published'      ? 'sage' :
    lifecycle === 'pre-registered' ? 'brass' :
    lifecycle === 'draft'          ? 'neutral' :
    lifecycle === 'revised'        ? 'cognac' :
    lifecycle === 'retracted'      ? 'oxblood' :
    'neutral';

  const visibilityTone =
    visibility === 'private'        ? 'neutral' :
    visibility === 'shared-link'    ? 'brass' :
    visibility === 'public-peek'    ? 'sage' :
    visibility === 'public-collab'  ? 'plum' :
    'neutral';

  const otherVersions = allVersions.filter((v) => v.meta.version !== version);

  return (
    <div className="finding-version-status">
      <div className="finding-version-row">
        <span className={`finding-version-badge finding-version-badge-${lifecycleTone}`}>
          {version} · {humanizeLifecycle(lifecycle)}
        </span>
        {visibility && (
          <span className={`finding-visibility-badge finding-visibility-badge-${visibilityTone}`}>
            {humanizeVisibility(visibility)}
          </span>
        )}
        {prevVersion && (
          <span className="finding-version-link-row">
            supersedes <code className="finding-version-handle">{prevVersion}</code>
          </span>
        )}
        {revisedBy && (
          <span className="finding-version-link-row">
            superseded by <code className="finding-version-handle">{revisedBy}</code>
          </span>
        )}
      </div>

      {retractionNote && (
        <div className="finding-version-retraction">
          <div className="finding-version-retraction-eyebrow">Retraction</div>
          <p>{retractionNote}</p>
        </div>
      )}

      {otherVersions.length > 0 && (
        <div className="finding-version-history">
          <span className="finding-version-history-eyebrow">All versions:</span>
          {allVersions.map((v) => (
            <a
              key={v.key}
              href={`/findings/${v.key}`}
              className={
                v.meta.version === version
                  ? 'finding-version-link finding-version-link-current'
                  : 'finding-version-link'
              }
            >
              {v.meta.version}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * "What changed" banner — shown when a version supersedes another.
 * Renders only if meta.whatChanged is populated.
 */
export function WhatChangedBanner({ meta }) {
  if (!meta?.whatChanged) return null;
  return (
    <aside className="finding-what-changed">
      <div className="finding-what-changed-eyebrow">What changed in {meta.version}</div>
      <p>{meta.whatChanged}</p>
      {meta.prevVersion && (
        <div className="finding-what-changed-link">
          Prior version: <code>{meta.prevVersion}</code>
        </div>
      )}
    </aside>
  );
}

function humanizeLifecycle(lifecycle) {
  if (lifecycle === 'pre-registered') return 'Pre-registered';
  if (lifecycle === 'published')      return 'Published';
  if (lifecycle === 'draft')          return 'Draft';
  if (lifecycle === 'revised')        return 'Revised (superseded)';
  if (lifecycle === 'retracted')      return 'Retracted';
  return lifecycle || 'Unknown';
}

function humanizeVisibility(visibility) {
  if (visibility === 'private')        return 'Private';
  if (visibility === 'shared-link')    return 'Shared by link';
  if (visibility === 'public-peek')    return 'Public · read-only';
  if (visibility === 'public-collab')  return 'Public · collab';
  return visibility || 'Unknown';
}
